/**
 * ShareDialog
 *
 * Hands the customer a link to their design. Two links exist per design, one per
 * permission, so what a recipient can do is decided by which URL they hold and can
 * never change under them. Switching the radio swaps which link is shown; it does
 * not re-permission a link already sent.
 *
 * Saves before minting: /preview renders the SERVER copy, so an unsaved edit would
 * not be in the shared link.
 *
 * Props:
 * - show, onClose
 * - cartOrderId: the design to share
 * - onBeforeShare: async () => response. Normally Header's saveProjectIfDirty. A falsy
 *   or { error } result aborts, so a gated or failed save never produces a link to a
 *   stale design.
 * - existingCodes: the loaded design document (projectSetup.themeDetails). The view
 *   endpoint already returns both share codes with the project, so a design that has
 *   been shared before needs NO network call to open this dialog. shareLink is only
 *   hit when a code is genuinely missing, or when the user resets one.
 */

import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Spinner, Form } from "react-bootstrap";
import styled from "styled-components";
import { apiPost } from "../../library/utils/common-services/apiCall.js";
import { ENDPOINTS } from "../../library/utils/constants/apiurl.js";

const StyledModal = styled(Modal)`
  z-index: 99999 !important;

  .modal-content {
    border-radius: 12px;
    border: none;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  }

  .modal-header {
    border-bottom: 1px solid #eee;
    padding: 16px 20px;
    background: linear-gradient(135deg, var(--primary) 0%, color-mix(in srgb, var(--primary) 80%, #000) 100%);
    border-radius: 12px 12px 0 0;
  }

  .modal-title {
    font-weight: 600;
    color: white;
    font-size: 18px;
  }

  .modal-body {
    padding: 20px;
    font-size: 15px;
    color: #333;
  }

  .modal-footer {
    border-top: 1px solid #eee;
    padding: 12px 20px;
    gap: 10px;
  }

  .btn-close {
    filter: brightness(0) invert(1);
  }
`;

const OptionRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid ${(p) => (p.selected ? "var(--primary)" : "#dee2e6")};
  background: ${(p) => (p.selected ? "var(--secondary, #eff8ff)" : "#fff")};
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 10px;

  input {
    margin-top: 3px;
    flex-shrink: 0;
  }
`;

const OptionTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 1.3;
`;

const OptionHint = styled.div`
  font-size: 12px;
  color: #6c757d;
  line-height: 1.4;
  margin-top: 2px;
`;

const LinkRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;

  .form-control {
    font-size: 12px;
    border-radius: 8px;
    background: #f8f9fa;
  }
`;

const QuietButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  color: #6c757d;
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

// Edit links point straight at the editor and view links at the preview route, so the
// recipient lands where they belong without a redirect flash. Both routes accept either
// code and bounce a mismatch to the other, so a link is never a dead end. The code stays
// in the URL in both cases: writing the cart order id there would let an edit recipient
// keep access forever, even after the owner resets the link.
const buildLink = (code, mode) =>
  code
    ? `${window.location.origin}${mode === "edit" ? "/?s=" : "/preview?s="}${encodeURIComponent(code)}`
    : "";

const ShareDialog = ({ show, onClose, cartOrderId, onBeforeShare, existingCodes }) => {
  const [mode, setMode] = useState("view");
  const [codes, setCodes] = useState({ view: "", edit: "" });
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  // Props are read through a ref, and `show` is the ONLY dependency below.
  // `onBeforeShare` is Header's saveProject, a new function identity on every Header
  // render. With it in the dep array the save's own re-render re-ran this effect: the
  // cleanup cancelled the in-flight request so its `finally` skipped setLoading(false),
  // and the re-run bailed on the already-open guard. The request succeeded and the
  // dialog span forever. Do not move these back into the dep array.
  const latestRef = useRef({ cartOrderId, onBeforeShare, existingCodes });
  latestRef.current = { cartOrderId, onBeforeShare, existingCodes };

  // Codes already minted in THIS session. redux `themeDetails` was loaded before the
  // first mint, so without this the second open of the dialog would hit shareLink
  // again even though nothing changed.
  const codesRef = useRef(codes);
  codesRef.current = codes;

  useEffect(() => {
    // Effect only re-runs when `show` flips, so reaching here IS "just opened".
    if (!show) return;

    let cancelled = false;
    setMode("view");
    // Deliberately NOT clearing `codes`: they survive close/reopen so a second open
    // in the same session is also free. codesRef below is what the async body reads.
    setError("");
    setCopied(false);
    setConfirmingReset(false);
    setLoading(true);

    (async () => {
      const { cartOrderId: id, onBeforeShare: prepare, existingCodes: known } = latestRef.current;
      try {
        if (prepare) {
          const saved = await prepare();
          if (!saved || saved.error) {
            if (!cancelled) {
              setError("Could not save your design, so the link was not created. Please try again.");
            }
            return;
          }
        }

        // Already-minted codes come back with the project itself (view returns the
        // whole design document), and anything minted this session is still in state.
        // Only hit shareLink when a code is genuinely missing.
        const held = codesRef.current;
        if (held.view && held.edit) {
          return;
        }
        if (known?.share_view_code && known?.share_edit_code) {
          setCodes({ view: known.share_view_code, edit: known.share_edit_code });
          return;
        }

        const response = await apiPost(ENDPOINTS.shareLink, { cart_order_id: id });
        if (cancelled) return;
        if (response?.status === 1 && response.items) {
          setCodes({
            view: response.items.share_view_code || "",
            edit: response.items.share_edit_code || "",
          });
        } else {
          setError(response?.message || "Could not create the link. Please try again.");
        }
      } catch (err) {
        if (!cancelled) setError("Could not create the link. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show]);

  const link = buildLink(codes[mode], mode);

  const handleCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard needs a secure context; the link still has to reach the user.
      window.prompt("Copy this link:", link);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    setError("");
    try {
      const response = await apiPost(ENDPOINTS.shareLink, {
        cart_order_id: cartOrderId,
        regenerate: mode,
      });
      if (response?.status === 1 && response.items) {
        setCodes({
          view: response.items.share_view_code || "",
          edit: response.items.share_edit_code || "",
        });
        setConfirmingReset(false);
      } else {
        setError(response?.message || "Could not reset the link. Please try again.");
      }
    } catch {
      setError("Could not reset the link. Please try again.");
    } finally {
      setResetting(false);
    }
  };

  const busy = loading || resetting;

  return (
    <StyledModal show={show} onHide={busy ? undefined : onClose} centered backdrop={busy ? "static" : true}>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Share your design</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <OptionRow selected={mode === "view"}>
          <input
            type="radio"
            name="share-mode"
            checked={mode === "view"}
            onChange={() => {
              setMode("view");
              setConfirmingReset(false);
            }}
            disabled={busy}
          />
          <div>
            <OptionTitle>View only</OptionTitle>
            <OptionHint>They can look at your design but cannot change it.</OptionHint>
          </div>
        </OptionRow>

        <OptionRow selected={mode === "edit"}>
          <input
            type="radio"
            name="share-mode"
            checked={mode === "edit"}
            onChange={() => {
              setMode("edit");
              setConfirmingReset(false);
            }}
            disabled={busy}
          />
          <div>
            <OptionTitle>Can edit</OptionTitle>
            <OptionHint>
              They can change this design and place the order. Only share this with
              people you trust.
            </OptionHint>
          </div>
        </OptionRow>

        {loading && (
          <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: 13 }}>
            <Spinner animation="border" size="sm" /> Saving your design and creating the link...
          </div>
        )}

        {!loading && error && (
          <div style={{ fontSize: 13, color: "#dc3545" }}>{error}</div>
        )}

        {!loading && !error && (
          <>
            <LinkRow>
              <Form.Control readOnly value={link} onFocus={(e) => e.target.select()} />
              <Button variant="primary" onClick={handleCopy} style={{ borderRadius: 8, minWidth: 84 }}>
                {copied ? "Copied" : "Copy"}
              </Button>
            </LinkRow>

            <div style={{ marginTop: 12 }}>
              {confirmingReset ? (
                <div style={{ fontSize: 12, color: "#6c757d" }}>
                  Anyone you already sent this link to will lose access.{" "}
                  <QuietButton onClick={handleReset} disabled={resetting} style={{ color: "#dc3545" }}>
                    {resetting ? "Resetting..." : "Reset it"}
                  </QuietButton>{" "}
                  <QuietButton onClick={() => setConfirmingReset(false)} disabled={resetting}>
                    Cancel
                  </QuietButton>
                </div>
              ) : (
                <QuietButton onClick={() => setConfirmingReset(true)}>
                  Reset this link
                </QuietButton>
              )}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={busy} style={{ borderRadius: 6 }}>
          Done
        </Button>
      </Modal.Footer>
    </StyledModal>
  );
};

export default ShareDialog;
